<template>
  <mp-box>
    <page-header :breadcrumbs="breadcrumbs" :title="calibrationName" />
    <content-wrapper>
      <template #content>
        <mp-flex gap="2" mb="6">
          <mp-box max-w="100%" w="520px">
            You have been invited to a group calibration. Please review each group according to its assigned period.
          </mp-box>
        </mp-flex>
        <mp-table-container width="100%" overflow="scroll">
          <mp-table>
            <mp-table-head>
              <mp-table-row>
                <mp-table-cell as="th" @click.native="updateSort('name')">
                  <mp-flex align-items="center" gap="1" cursor="pointer">
                    <mp-text font-weight="semibold">Group name</mp-text>
                    <mp-icon :name="sort === 'name-asc' ? 'sort-ascending' : sort === 'name-desc' ? 'sort-descending' : 'sort-default'" />
                  </mp-flex>
                </mp-table-cell>
                <mp-table-cell as="th" @click.native="updateSort('date')">
                  <mp-flex align-items="center" gap="1" cursor="pointer">
                    <mp-text font-weight="semibold">Calibration period</mp-text>
                    <mp-icon :name="sort === 'date-asc' ? 'sort-ascending' : sort === 'date-desc' ? 'sort-descending' : 'sort-default'" />
                  </mp-flex>
                </mp-table-cell>
                <mp-table-cell as="th">Employee(s)</mp-table-cell>
                <mp-table-cell as="th" @click.native="updateSort('total_contributor')">
                  <mp-flex align-items="center" gap="1" cursor="pointer">
                    <mp-text font-weight="semibold">Contributor</mp-text>
                    <mp-tooltip position="bottom" label="The contributor who is responsible for conducting the calibration in each group.">
                      <mp-icon name="info" size="sm" ml="1" />
                    </mp-tooltip>
                    <mp-icon
                      :name="
                        sort === 'total_contributor-asc' ? 'sort-ascending' : sort === 'total_contributor-desc' ? 'sort-descending' : 'sort-default'
                      "
                    />
                  </mp-flex>
                </mp-table-cell>
                <mp-table-cell as="th" @click.native="updateSort('status')">
                  <mp-flex align-items="center" gap="1" cursor="pointer">
                    <mp-text font-weight="semibold">Status</mp-text>
                    <mp-icon :name="sort === 'status-asc' ? 'sort-ascending' : sort === 'status-desc' ? 'sort-descending' : 'sort-default'" />
                  </mp-flex>
                </mp-table-cell>
                <mp-table-cell as="th" />
              </mp-table-row>
            </mp-table-head>
            <mp-table-body>
              <mp-table-row v-for="group in groups" :key="group.id">
                <mp-table-cell as="td">{{ group.name }} </mp-table-cell>
                <mp-table-cell as="td">{{ formatDateRange(group.start_date, group.end_date) }}</mp-table-cell>
                <mp-table-cell as="td">{{ group.total_employees }} employees</mp-table-cell>
                <mp-table-cell as="td">
                  <mp-text is-link @click.native="showContributors(group)">{{ group.contributors.length }} Contributors</mp-text>
                </mp-table-cell>
                <mp-table-cell as="td">
                  <mp-badge variant="subtle" :variant-color="colorMap[group.status]">{{ statusNameMap[group.status] }}</mp-badge>
                </mp-table-cell>
                <mp-table-cell as="td">
                  <mp-button variant="outline" :is-disabled="group.status === CALIBRATION_STATUS.NOT_STARTED" @click="goToDetail(group)">
                    View result
                  </mp-button>
                </mp-table-cell>
              </mp-table-row>
            </mp-table-body>
          </mp-table>
        </mp-table-container>
        <mp-flex v-if="isLoading" align-items="center" justify-content="center" py="10">
          <mp-spinner />
        </mp-flex>
        <mp-box v-if="!isLoading && !groups.length" py="5">
          <blank-slate
            :blank-slate-data="{
              title: 'No calibration groups found',
              sub_title: 'There are no calibration groups available in this session.',
              img: $store.state.assets.illustrationEmptyStateResults,
            }"
          />
        </mp-box>
        <pagination v-if="groups.length" :metas="metas" :disabled="isLoading" @update="onPagination" />

        <mp-modal
          :is-open="isContributorModalOpen"
          :on-close="() => (isContributorModalOpen = false)"
          close-on-overlay-click
          scroll-behavior="inside"
        >
          <mp-modal-content max-h="100%" height="auto!important">
            <mp-modal-header>Contributors</mp-modal-header>
            <mp-modal-close-button />
            <mp-modal-body pt="0">
              <mp-box position="sticky" top="0" bg="white" z-index="1" pt="4" pb="4">
                <mp-text font-weight="semibold">{{ selectedGroup.name }}</mp-text>
              </mp-box>
              <contributor-list-view :contributors="selectedGroup.contributors" />
            </mp-modal-body>
          </mp-modal-content>
          <mp-modal-overlay />
        </mp-modal>
      </template>
    </content-wrapper>
  </mp-box>
</template>

<script>
import PageHeader from '@/components/layouts/PageHeader';
import ContentWrapper from '@/components/layouts/ContentWrapper';
import { formatDateRange } from '@/helpers/date';
import Pagination from '@/components/Pagination';
import { buildParams } from '@/helpers/url';
import { sortMixin } from '@/mixins/table';
import { alertMixin } from '@/mixins/general';
import roleMixin from '@/mixins/role';
import BlankSlate from '@/components/general/BlankSlate';
import ContributorListView from '@/components/ContributorListView';

const CALIBRATION_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  COMPLETED: 'COMPLETED',
};

export default {
  name: 'ReviewsCalibrationGroupList',
  components: {
    PageHeader,
    ContentWrapper,
    Pagination,
    BlankSlate,
    ContributorListView,
  },
  mixins: [sortMixin, alertMixin, roleMixin],
  data() {
    return {
      breadcrumbs: [{ label: 'Calibration', url: { name: 'calibration_list' } }],
      CALIBRATION_STATUS,
      isLoading: false,
      groups: [],
      calibrationName: '',
      metas: {
        current_page: 1,
        last_page: 1,
        per_page: 10,
        limit_per_page: 10,
        from: 1,
        to: 1,
        total: 1,
      },
      colorMap: {
        IN_PROGRESS: 'orange',
        NOT_STARTED: 'gray',
        COMPLETED: 'green',
      },
      statusNameMap: {
        IN_PROGRESS: 'In Progress',
        NOT_STARTED: 'Not Started',
        COMPLETED: 'Completed',
      },
      isContributorModalOpen: false,
      selectedGroup: {
        name: '',
        contributors: [],
      },
    };
  },
  computed: {
    calibrationUuid() {
      return this.$route.params.uuid;
    },
  },
  mounted() {
    this.fetchGroups();
  },
  methods: {
    formatDateRange,
    onPagination({ page, limit }) {
      this.metas.current_page = page;
      this.metas.per_page = limit;
      this.metas.limit_per_page = limit;
      this.fetchGroups();
    },
    async fetchGroups() {
      try {
        this.isLoading = true;
        const params = buildParams({
          page: this.metas.current_page,
          limit_per_page: this.metas.limit_per_page,
          sort_field: this.sort,
        });
        const { data } = await this.$axios.get(`${this.$newEndPoint}/calibration/${this.calibrationUuid}/groups?${params}`);
        this.groups = data.contents.data;
        this.calibrationName = data.contents.calibration?.name || 'N/A';
        this.metas = data.contents.meta;
      } catch (e) {
        this.alertToast('Error to fetch calibration groups', 'danger');
      } finally {
        this.isLoading = false;
      }
    },
    showContributors(group) {
      this.selectedGroup = group;
      this.isContributorModalOpen = true;
    },
    goToDetail(group) {
      this.$router.push({
        name: 'calibration_detail',
        params: { id: this.calibrationUuid },
        query: { group_uuid: group.uuid },
      });
    },
    updateSort(field) {
      this.updateFilterSort(field);
      this.fetchGroups();
    },
  },
};
</script>
